import { ACTIVITY_TIMER_TAB_ID } from '../../../services/activityTimerPersistence'

export const ACTIVITY_TYPES = [
  { value: 'STUDY', label: '학습' },
  { value: 'REST', label: '휴식' },
  { value: 'MEAL', label: '식사' },
  { value: 'SLEEP', label: '수면' },
  { value: 'ETC', label: '기타' },
]

export const TIMER_STATUS = {
  IDLE: 'idle',
  PREPARING: 'preparing',
  RUNNING: 'running',
  PAUSED: 'paused',
  UNSAVED: 'unsaved',
  SAVING: 'saving',
  SAVE_UNKNOWN: 'save-unknown',
}

export const TIMER_ACTION = {
  INITIALIZE: 'activityTimer/initialize',
  SET_OWNER: 'activityTimer/setOwner',
  PREPARE: 'activityTimer/prepare',
  UPDATE_DRAFT: 'activityTimer/updateDraft',
  START: 'activityTimer/start',
  PAUSE: 'activityTimer/pause',
  RESUME: 'activityTimer/resume',
  STOP: 'activityTimer/stop',
  SAVE_START: 'activityTimer/saveStart',
  SAVE_FAILURE: 'activityTimer/saveFailure',
  CONFIRM_SAVE_MISSING: 'activityTimer/confirmSaveMissing',
  SAVE_SUCCESS: 'activityTimer/saveSuccess',
  DISCARD: 'activityTimer/discard',
  SYNC_EXTERNAL: 'activityTimer/syncExternal',
}

const createIdleTimerState = (savedState = {}) => ({
  status: TIMER_STATUS.IDLE,
  dailyLogType: 'STUDY',
  contents: '',
  source: null,
  startedAt: null,
  endedAt: null,
  focusAccumulatedMs: 0,
  focusStartedAt: null,
  lastSavedLogOn: savedState.lastSavedLogOn || null,
  saveVersion: savedState.saveVersion || 0,
})

export const initialTimerState = createIdleTimerState()

export function activityTimerReducer(state, action) {
  switch (action.type) {
    case TIMER_ACTION.PREPARE:
    case 'PREPARE':
      if (state.status !== TIMER_STATUS.IDLE) return state
      return {
        ...state,
        status: TIMER_STATUS.PREPARING,
        dailyLogType: action.dailyLogType || 'STUDY',
        contents: action.contents || '',
        source: action.source || null,
      }
    case TIMER_ACTION.UPDATE_DRAFT:
    case 'UPDATE_DRAFT':
      if (![TIMER_STATUS.PREPARING, TIMER_STATUS.UNSAVED].includes(state.status)) return state
      return {
        ...state,
        ...(action.contents !== undefined ? { contents: action.contents } : {}),
        ...(action.dailyLogType !== undefined && state.status === TIMER_STATUS.PREPARING
          ? { dailyLogType: action.dailyLogType }
          : {}),
      }
    case TIMER_ACTION.START:
    case 'START':
      if (state.status !== TIMER_STATUS.PREPARING) return state
      return {
        ...state,
        status: TIMER_STATUS.RUNNING,
        startedAt: action.now,
        endedAt: null,
        focusAccumulatedMs: 0,
        focusStartedAt: state.dailyLogType === 'STUDY' ? action.now : null,
      }
    case TIMER_ACTION.PAUSE:
    case 'PAUSE':
      if (state.status !== TIMER_STATUS.RUNNING || state.dailyLogType !== 'STUDY') return state
      return {
        ...state,
        status: TIMER_STATUS.PAUSED,
        focusAccumulatedMs: state.focusAccumulatedMs + Math.max(0, action.now - state.focusStartedAt),
        focusStartedAt: null,
      }
    case TIMER_ACTION.RESUME:
    case 'RESUME':
      if (state.status !== TIMER_STATUS.PAUSED || state.dailyLogType !== 'STUDY') return state
      return { ...state, status: TIMER_STATUS.RUNNING, focusStartedAt: action.now }
    case TIMER_ACTION.STOP:
    case 'STOP': {
      if (![TIMER_STATUS.RUNNING, TIMER_STATUS.PAUSED].includes(state.status)) return state
      const finalFocusMs = state.dailyLogType === 'STUDY' && state.focusStartedAt !== null
        ? state.focusAccumulatedMs + Math.max(0, action.now - state.focusStartedAt)
        : state.focusAccumulatedMs
      return {
        ...state,
        status: TIMER_STATUS.UNSAVED,
        endedAt: action.now,
        focusAccumulatedMs: finalFocusMs,
        focusStartedAt: null,
      }
    }
    case TIMER_ACTION.SAVE_START:
    case 'SAVE_START':
      return state.status === TIMER_STATUS.UNSAVED
        ? { ...state, status: TIMER_STATUS.SAVING }
        : state
    case TIMER_ACTION.SAVE_FAILURE:
    case 'SAVE_FAILURE':
      return state.status === TIMER_STATUS.SAVING
        ? { ...state, status: TIMER_STATUS.UNSAVED }
        : state
    case TIMER_ACTION.CONFIRM_SAVE_MISSING:
    case 'CONFIRM_SAVE_MISSING':
      return state.status === TIMER_STATUS.SAVE_UNKNOWN
        ? { ...state, status: TIMER_STATUS.UNSAVED }
        : state
    case TIMER_ACTION.SAVE_SUCCESS:
    case 'SAVE_SUCCESS':
      return createIdleTimerState({
        lastSavedLogOn: action.logOn,
        saveVersion: state.saveVersion + 1,
      })
    case TIMER_ACTION.DISCARD:
    case 'DISCARD':
      return createIdleTimerState(state)
    case TIMER_ACTION.SYNC_EXTERNAL:
    case 'SYNC_EXTERNAL':
      return action.state || createIdleTimerState(state)
    default:
      return state
  }
}

export function getTimerDurations(state, now = Date.now()) {
  if (state.startedAt === null) {
    return { activityTimeSec: 0, focusTimeSec: state.dailyLogType === 'STUDY' ? 0 : null }
  }

  const effectiveEnd = state.endedAt || now
  const activityTimeSec = Math.max(0, Math.floor((effectiveEnd - state.startedAt) / 1000))
  const currentFocusMs = state.dailyLogType === 'STUDY' && state.focusStartedAt !== null
    ? Math.max(0, now - state.focusStartedAt)
    : 0
  const focusTimeSec = state.dailyLogType === 'STUDY'
    ? Math.max(0, Math.floor((state.focusAccumulatedMs + currentFocusMs) / 1000))
    : null

  return { activityTimeSec, focusTimeSec }
}

const initialActivityTimerState = {
  timer: initialTimerState,
  ownerId: null,
  tabId: ACTIVITY_TIMER_TAB_ID,
}

export function activityTimerRuntimeReducer(state = initialActivityTimerState, action) {
  switch (action.type) {
    case TIMER_ACTION.INITIALIZE:
      return {
        timer: action.state || createIdleTimerState(state.timer),
        ownerId: action.ownerId || null,
        tabId: action.tabId || ACTIVITY_TIMER_TAB_ID,
      }
    case TIMER_ACTION.SET_OWNER:
      return { ...state, ownerId: action.ownerId || null }
    case TIMER_ACTION.SYNC_EXTERNAL:
      return {
        ...state,
        timer: action.state || createIdleTimerState(state.timer),
        ownerId: action.ownerId || null,
      }
    case TIMER_ACTION.PREPARE: {
      const timer = activityTimerReducer(state.timer, action)
      return timer === state.timer ? state : { ...state, timer, ownerId: state.tabId }
    }
    case TIMER_ACTION.SAVE_SUCCESS:
    case TIMER_ACTION.DISCARD:
      return {
        ...state,
        timer: activityTimerReducer(state.timer, action),
        ownerId: null,
      }
    default: {
      const timer = activityTimerReducer(state.timer, action)
      return timer === state.timer ? state : { ...state, timer }
    }
  }
}

export const selectActivityTimerRuntime = (state) => state.activityTimer
